import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth';

// Layouts
import AuthLayout from './layouts/AuthLayout';
import AppLayout from './layouts/AppLayout';

// Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import CheckEmailPage from './pages/auth/CheckEmailPage';
import TwoFactorPage from './pages/auth/TwoFactorPage';
import ResetTwoFactorPage from './pages/auth/ResetTwoFactorPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import OKRPage from './pages/OKRPage';
import BoardsPage from './pages/BoardsPage';
import BoardPage from './pages/BoardPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import FeedbackPage from './pages/FeedbackPage';
import LandingPage from './pages/LandingPage';
import DocsPage from './pages/DocsPage';
import BlogPage, { BlogPostPage } from './pages/BlogPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import HelpCenterPage from './pages/HelpCenterPage';
import TeamSettingsPage from './pages/TeamSettingsPage';
import JoinWorkspacePage from './pages/JoinWorkspacePage';
import TeamAnalyticsPage from './pages/TeamAnalyticsPage';
import ReferralPage from './pages/ReferralPage';
import PricingPage from './pages/PricingPage';
import AlignmentPage from './pages/AlignmentPage';
import NotesPage from './pages/NotesPage';
import ReportsPage from './pages/ReportsPage';
import GoogleTasksIntegrationPage from './pages/GoogleTasksIntegrationPage';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated } = useAuthStore();

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
}

// Public Route wrapper (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated } = useAuthStore();

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
}

export default function App() {
    const [isHydrated, setIsHydrated] = useState(false);

    // Wait for Zustand to hydrate from localStorage
    useEffect(() => {
        // Zustand persist middleware hydrates synchronously,
        // but we add a small delay to ensure React Query also catches up
        const timer = setTimeout(() => setIsHydrated(true), 50);
        return () => clearTimeout(timer);
    }, []);

    // Show loading while hydrating
    if (!isHydrated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
                <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
            </div>
        );
    }
    return (
        <BrowserRouter>
            <Routes>
                {/* Public routes */}
                <Route element={<AuthLayout />}>
                    <Route
                        path="/login"
                        element={
                            <PublicRoute>
                                <LoginPage />
                            </PublicRoute>
                        }
                    />
                    <Route
                        path="/register"
                        element={
                            <PublicRoute>
                                <RegisterPage />
                            </PublicRoute>
                        }
                    />
                </Route>

                {/* Email verification & 2FA routes - no layout */}
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/check-email" element={<CheckEmailPage />} />
                <Route path="/2fa-verify" element={<TwoFactorPage />} />
                <Route path="/2fa-reset" element={<ResetTwoFactorPage />} />

                {/* Password reset routes - no layout */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Protected routes */}
                <Route
                    element={
                        <ProtectedRoute>
                            <AppLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/goals" element={<OKRPage />} />
                    <Route path="/boards" element={<BoardsPage />} />
                    <Route path="/boards/:boardId" element={<BoardPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/settings/team" element={<TeamSettingsPage />} />
                    <Route path="/team-analytics" element={<TeamAnalyticsPage />} />
                    <Route path="/feedback" element={<FeedbackPage />} />
                    <Route path="/referral" element={<ReferralPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/alignment" element={<AlignmentPage />} />
                    <Route path="/notes" element={<NotesPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/integrations/google-tasks" element={<GoogleTasksIntegrationPage />} />
                    <Route path="/integrations/google-tasks/callback" element={<GoogleTasksIntegrationPage />} />
                </Route>

                {/* Landing page - shown when not authenticated */}
                <Route
                    path="/"
                    element={
                        <PublicRoute>
                            <LandingPage />
                        </PublicRoute>
                    }
                />

                {/* Docs page - accessible to everyone */}
                <Route path="/docs" element={<DocsPage />} />

                {/* Blog pages - accessible to everyone */}
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:postId" element={<BlogPostPage />} />

                {/* Legal and Support pages - accessible to everyone */}
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/help" element={<HelpCenterPage />} />

                {/* Join workspace - needs auth check inside component */}
                <Route path="/join/:token" element={<JoinWorkspacePage />} />

                {/* Catch all - redirect to dashboard if logged in, else to landing */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
    );
}
